import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import { CountriesOfRoutingType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankCountryOfRoutingOfConsignment } from 'features/declaration/models/blankItems'
import FormFieldArray from '../../../../components/p5/FormFieldArray'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import Input from '../../../../components/p5/Input'
import FormField from '../../../../components/p5/FormField'
import FormSelect from '../../../../components/p5/FormSelect'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'

const scope = 'countriesOfRouting'

function CountriesOfRouting() {
  const { t } = useTranslation()

  const { control } = useFormContext<HouseConsignmentFormValues>()

  const {
    softRemove,
    appendWithSequence,
    visibleFields,
  } = useSoftDeleteFieldArray<HouseConsignmentFormValues, CountriesOfRoutingType>({
    control,
    name: 'countriesOfRouting',
  })

  const [, countriesOptions] = useCodelist('COUNTRIES')

  const isDifferentCountriesOfRouting = useWatch({
    control,
    name: 'differentCountriesOfRouting',
  })

  const paths = useMemo(
    () => [
      scope,
      'differentCountriesOfRouting',
      ...visibleFields.map((f) => `${scope}.${f.index}.country`),
    ],
    [visibleFields],
  )
  const fieldRequirements = useDeclarationFieldRequirements<HouseConsignmentFormValues>('houseConsignment', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-countries-of-routing"
        headingLabel={t('declaration.p5.countriesOfRouting')}
        fieldRule={fieldRequirements[scope]}
      >
        <div className="row mb-2">
          <div className="d-flex mb-2 col-8">
            <Input
              field="differentCountriesOfRouting"
              type="checkbox"
              labelKey="Different countries of routing from consignment"
              fieldRule={fieldRequirements.differentCountriesOfRouting}
            />
          </div>
        </div>
        {isDifferentCountriesOfRouting && (
          <>
            {visibleFields.map((fieldWithIndex) => {
              const countryOfRoutingOfConsignment = fieldWithIndex.field
              const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

              return (
                <FormFieldArray
                  key={`countriesOfRouting_${countryOfRoutingOfConsignment.id}`}
                  titleKey="declaration.p5.sequenceNumber"
                  removeItem={() => softRemove(fieldWithIndex.index)}
                  sequenceNumber={countryOfRoutingOfConsignment.sequenceNumber + 1}
                  hidden={countryOfRoutingOfConsignment.deleted}
                >
                  <FormField
                    labelKey="declaration.p5.countryOfRouting"
                    fieldRule={fieldRequirements[`${innerItemScope}.country`]}
                  >
                    <FormSelect
                      <HouseConsignmentFormValues>
                      field={`${innerItemScope}.country`}
                      labelKey="declaration.p5.countryOfRouting"
                      options={countriesOptions}
                      type="sync"
                      fieldRule={fieldRequirements[`${innerItemScope}.country`]}
                    />
                  </FormField>
                </FormFieldArray>
              )
            })}
            <div className="row">
              <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
                <button
                  type="button"
                  className="btn btn-sm btn-primary mt-2"
                  onClick={() => appendWithSequence(blankCountryOfRoutingOfConsignment)}
                  disabled={isAddForbidden}
                >
                  <i className="fa fa-plus me-2" />
                  {t('declaration.p5.addNewRoutingCountry')}
                </button>
              </div>
            </div>
          </>
        )}
      </FormCollapsibleColumn>
    </section>
  )
}

export default CountriesOfRouting
